import React, { useId, useRef, useState } from "react";
import { changeShort, plural, usualLine } from "@/helpers/resultsCopy";
import {
  CHART_HEIGHT,
  chartPoints,
  cumulative,
  formatDay,
  pointX,
  pointY,
  pointerIndex,
  seriesFor,
  smoothAreaPath,
  smoothPath,
  xLabelIndexes,
  yTicks,
} from "@/helpers/resultsChart";

const Arrow = ({ up }) => (
  <svg className="results-tile__arrow" viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
    <circle cx="8" cy="8" r="8" />
    <path d={up ? "M8 4.2 11.4 8H9.1v3.8H6.9V8H4.6z" : "M8 11.8 4.6 8h2.3V4.2h2.2V8h2.3z"} />
  </svg>
);

const TileValue = ({ value }) => (
  <span className="results-tile__value">{typeof value === "number" ? value.toLocaleString("en-US") : value}</span>
);

export const MetricTabs = ({ tiles, selected, onSelect, label = "Metrics", days }) => {
  const refs = useRef([]);
  const move = (event, index) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (!step) return;
    event.preventDefault();
    const next = (index + step + tiles.length) % tiles.length;
    onSelect(tiles[next].key);
    refs.current[next]?.focus();
  };

  return (
    <div className="results-tiles" role="tablist" aria-label={label} style={{ "--results-tiles": tiles.length }}>
      {tiles.map((tile, index) => {
        const delta = tile.change ? changeShort(tile.change) : null;
        const usual = tile.change && days ? usualLine(tile.change, days) : null;
        const on = tile.key === selected;
        return (
          <button
            key={tile.key}
            ref={(node) => {
              refs.current[index] = node;
            }}
            type="button"
            role="tab"
            id={`results-tile-${tile.key}`}
            aria-selected={on}
            tabIndex={on ? 0 : -1}
            className={`results-tile${on ? " is-on" : ""}`}
            onClick={() => onSelect(tile.key)}
            onKeyDown={(event) => move(event, index)}
          >
            <span className="results-tile__label">{tile.label}</span>
            <span className={`results-tile__figure${usual ? ` is-${usual.direction}` : ""}`}>
              <TileValue value={tile.value} />
              {on && usual && usual.direction !== "flat" ? <Arrow up={usual.direction === "up"} /> : null}
            </span>
            {on && usual ? (
              <span className={`results-tile__usual is-${usual.direction}`} aria-hidden="true">
                {usual.text}
              </span>
            ) : null}
            {delta && delta.direction !== "flat" ? (
              <span className="visually-hidden">{`${delta.text} vs the period before`}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
};

const MODES = [
  { key: "total", label: "Running total" },
  { key: "daily", label: "Per day" },
];

export const TrendChart = ({ series, metricKey, unit = ["", ""], tabId, days }) => {
  const plotRef = useRef(null);
  const gradientId = `results-trend-${useId().replace(/:/g, "")}`;
  const [hover, setHover] = useState(null);
  const [mode, setMode] = useState("total");
  const data = seriesFor(series, metricKey);
  if (!data) return null;

  const { days: dates } = data;
  const current = mode === "total" ? cumulative(data.current) : data.current;
  const previous = data.previous ? (mode === "total" ? cumulative(data.previous) : data.previous) : null;
  const ticks = yTicks([...current, ...(previous || [])], 4);
  const top = ticks[ticks.length - 1];
  const empty = data.current.every((value) => !value) && !(data.previous || []).some(Boolean);
  const total = data.current.reduce((sum, value) => sum + value, 0);
  const previousTotal = data.previous ? data.previous.reduce((sum, value) => sum + value, 0) : null;
  const pct = (value, size) => `${(value / size) * 100}%`;
  const points = chartPoints(current, top);
  const previousPoints = previous ? chartPoints(previous, top) : null;
  const published = Array.isArray(series?.published) && series.published.length === dates.length ? series.published : null;
  const compareLabel = days ? `Previous ${days} days` : "Period before";

  const track = (event) => {
    const rect = plotRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHover(pointerIndex({ clientX: event.clientX, left: rect.left, width: rect.width }, dates.length));
  };

  const plainDay = (value) => formatDay(value).replace(/\u00a0/g, " ");
  const summary = `${unit[1] || "Activity"} per day, ${plainDay(dates[0])} to ${plainDay(dates[dates.length - 1])}. ${total} in this period${
    previousTotal === null ? "" : `, ${previousTotal} in the period before`
  }.`;

  const at = hover;
  const hoverX = at === null ? 0 : pointX(at, dates.length);

  return (
    <div className="results-trend" role="tabpanel" aria-labelledby={tabId}>
      <div className="results-trend__bar">
        {previous && !empty ? (
          <p className="results-trend__legend" aria-hidden="true">
            <span className="is-current">This period</span>
            <span className="is-previous">{compareLabel}</span>
          </p>
        ) : (
          <span />
        )}
        <div className="results-trend__modes" role="radiogroup" aria-label="Chart view">
          {MODES.map((item) => (
            <button
              key={item.key}
              type="button"
              role="radio"
              aria-checked={mode === item.key}
              className={`results-trend__mode${mode === item.key ? " is-on" : ""}`}
              onClick={() => setMode(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
      <div
        className="results-trend__plot"
        ref={plotRef}
        onPointerMove={empty ? undefined : track}
        onPointerDown={empty ? undefined : track}
        onPointerLeave={() => setHover(null)}
      >
        <svg className="results-trend__grid-layer" viewBox={`0 0 1000 ${CHART_HEIGHT}`} preserveAspectRatio="none" aria-hidden="true">
          {ticks.map((tick) => (
            <line
              key={tick}
              className={`results-trend__grid${tick ? "" : " is-base"}`}
              x1="0"
              x2="1000"
              y1={pointY(tick, top)}
              y2={pointY(tick, top)}
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>
        <svg
          key={`${metricKey}-${mode}`}
          className="results-trend__series"
          viewBox={`0 0 1000 ${CHART_HEIGHT}`}
          preserveAspectRatio="none"
          role="img"
          aria-label={summary}
        >
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" className="results-trend__stop-top" />
              <stop offset="100%" className="results-trend__stop-bottom" />
            </linearGradient>
          </defs>
          {empty ? null : (
            <>
              {previousPoints ? <path className="results-trend__previous" d={smoothAreaPath(previousPoints)} /> : null}
              <path className="results-trend__area" d={smoothAreaPath(points)} fill={`url(#${gradientId})`} />
              <path className="results-trend__line" d={smoothPath(points)} vectorEffect="non-scaling-stroke" />
            </>
          )}
        </svg>
        {empty ? <p className="results-trend__empty">No activity in this period yet</p> : null}
        {at === null ? null : (
          <>
            <span className="results-trend__guide" style={{ left: pct(hoverX, 1000) }} aria-hidden="true" />
            <span
              className="results-trend__dot"
              style={{ left: pct(hoverX, 1000), top: pct(pointY(current[at], top), CHART_HEIGHT) }}
              aria-hidden="true"
            />
            <div className={`results-trend__tip${hoverX > 640 ? " is-left" : ""}`} style={{ left: pct(hoverX, 1000) }} role="status">
              <p className="results-trend__tip-date">{formatDay(dates[at])}</p>
              <p className="results-trend__tip-row">
                <span className="results-trend__key is-current" />
                {plural(current[at], unit[0], unit[1]).trim()}
              </p>
              {previous ? (
                <p className="results-trend__tip-row">
                  <span className="results-trend__key is-previous" />
                  {`${compareLabel}: ${previous[at]}`}
                </p>
              ) : null}
            </div>
          </>
        )}
      </div>
      <div className="results-trend__y" aria-hidden="true">
        {ticks.map((tick) => (
          <span key={tick} style={{ top: pct(pointY(tick, top), CHART_HEIGHT) }}>
            {tick.toLocaleString("en-US")}
          </span>
        ))}
      </div>
      <div className="results-trend__x" aria-hidden="true">
        {xLabelIndexes(dates.length).map((index) => (
          <span key={dates[index]} style={{ left: pct(pointX(index, dates.length), 1000) }}>
            {formatDay(dates[index])}
          </span>
        ))}
      </div>
      {published ? (
        <div className="results-trend__published" aria-label="Buyer Want-Listings you published">
          {published.map((count, index) =>
            count ? (
              <span
                key={dates[index]}
                className="results-trend__pub"
                style={{ left: pct(pointX(index, dates.length), 1000) }}
                title={`${formatDay(dates[index])}: ${plural(count, "Buyer Want-Listing", "Buyer Want-Listings")} published`}
              >
                {count}
              </span>
            ) : null,
          )}
        </div>
      ) : null}
    </div>
  );
};

export const Overview = ({ tiles, series, label, days, aside = null, below = null, seeMore = null }) => {
  const [selected, setSelected] = useState(tiles[0]?.key);
  const active = tiles.find((tile) => tile.key === selected) || tiles[0];
  if (!active) return null;
  const main = (
    <section className="results-card results-overview">
      <MetricTabs tiles={tiles} selected={active.key} onSelect={setSelected} label={label} days={days} />
      <TrendChart series={series} metricKey={active.key} unit={active.unit} tabId={`results-tile-${active.key}`} days={days} />
      {seeMore ? (
        <div className="results-overview__foot">
          <button type="button" className="results-more" onClick={seeMore.onClick}>
            {seeMore.label || "See more"}
          </button>
        </div>
      ) : null}
    </section>
  );
  if (!aside && !below) return main;
  return (
    <div className="results-studio">
      {main}
      {aside ? <div className="results-studio__side">{aside}</div> : null}
      {below ? <div className="results-studio__below">{below}</div> : null}
    </div>
  );
};
